/**
 * Google Analytics 4 Event Tracking
 * Comprehensive tracking for lead sources, CTAs, and user journey
 */

// Define custom event types
export type GAEvent =
  // Lead Generation Events
  | 'lead_submit'
  | 'contact_form_submit'
  | 'property_inquiry_submit'
  | 'phone_call_click'
  | 'whatsapp_click'
  | 'email_click'
  | 'schedule_viewing'

  // Property Interaction Events
  | 'property_view'
  | 'property_click'
  | 'property_favorite'
  | 'property_share'
  | 'property_image_view'
  | 'property_video_view'
  | 'property_tour_start'

  // Search & Filter Events
  | 'property_search'
  | 'filter_apply'
  | 'filter_reset'
  | 'sort_change'
  | 'location_search'

  // Navigation Events
  | 'page_view'
  | 'developer_view'
  | 'project_view'
  | 'subscription_view'

  // Engagement Events
  | 'scroll_depth'
  | 'time_on_page'
  | 'social_share'
  | 'print_property'

  // Conversion Events
  | 'signup'
  | 'login'
  | 'subscription_start'
  | 'subscription_payment'

export interface GAEventParams {
  // Lead Source Parameters
  source?: 'organic' | 'paid' | 'referral' | 'direct' | 'social' | 'email' | 'sms'
  medium?: string
  campaign?: string

  // Property Parameters
  property_id?: string
  property_title?: string
  property_slug?: string
  property_type?: string
  listing_type?: 'sale' | 'rent' | string
  property_price?: number
  property_location?: string
  developer_name?: string
  project_name?: string

  // User Interaction Parameters
  cta_type?: 'phone' | 'whatsapp' | 'email' | 'form' | 'chat' | 'messenger' | 'viber'
  cta_location?: string // e.g., 'property_detail', 'homepage', 'listing'
  cta_label?: string

  // Search Parameters
  search_term?: string
  filters_applied?: string[]
  sort_by?: string
  results_count?: number

  // Engagement Parameters
  scroll_percentage?: number
  time_spent?: number // seconds
  page_url?: string

  // Conversion Parameters
  value?: number
  currency?: string

  // Custom Parameters
  [key: string]: any
}

/**
 * Initialize Google Analytics
 * Note: Script loading is handled by GA4Script component
 * This function just sets up traffic source tracking
 */
export const initGA = (measurementId: string) => {
  if (typeof window === 'undefined') return

  // Capture traffic source on page load
  captureTrafficSource()

  console.log('GA4 tracking initialized for', measurementId)
}

/**
 * Track a custom event
 */
export const trackEvent = (eventName: GAEvent | string, params?: GAEventParams) => {
  if (typeof window === 'undefined' || !window.gtag) return

  // Add common parameters
  const enhancedParams = {
    ...params,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    page_title: document.title,
  }

  // Send to GA4
  window.gtag('event', eventName, enhancedParams)

  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('GA Event:', eventName, enhancedParams)
  }
}

/**
 * Track page view with enhanced parameters
 */
export const trackPageView = (url?: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return

  const params: GAEventParams = {
    page_url: url || window.location.href,
    page_title: title || document.title,
    page_path: window.location.pathname,
  }

  // Add traffic source info
  const trafficSource = getTrafficSource()
  if (trafficSource) {
    params.source = trafficSource.source
    params.medium = trafficSource.medium
    params.campaign = trafficSource.campaign
  }

  window.gtag('event', 'page_view', params)
}

/**
 * Capture and store traffic source from URL parameters
 */
const captureTrafficSource = () => {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)

  const source = params.get('utm_source') || params.get('source') || inferSource()
  const medium = params.get('utm_medium') || params.get('medium')
  const campaign = params.get('utm_campaign') || params.get('campaign')
  const referrer = document.referrer

  const trafficSource = {
    source,
    medium,
    campaign,
    referrer,
    landing_page: window.location.href,
    timestamp: new Date().toISOString(),
  }

  // Store in sessionStorage for later use
  sessionStorage.setItem('traffic_source', JSON.stringify(trafficSource))
}

/**
 * Get stored traffic source
 */
const getTrafficSource = () => {
  if (typeof window === 'undefined') return null

  const stored = sessionStorage.getItem('traffic_source')
  if (stored) {
    return JSON.parse(stored)
  }
  return null
}

/**
 * Infer traffic source from referrer
 */
const inferSource = (): 'organic' | 'direct' | 'referral' | 'social' => {
  if (typeof window === 'undefined') return 'direct'

  const referrer = document.referrer

  if (!referrer) return 'direct'

  // Social media domains
  const socialDomains = ['facebook.com', 'twitter.com', 'instagram.com', 'linkedin.com', 'tiktok.com', 'youtube.com']
  if (socialDomains.some(domain => referrer.includes(domain))) {
    return 'social'
  }

  // Search engines
  const searchEngines = ['google.com', 'bing.com', 'yahoo.com', 'baidu.com', 'duckduckgo.com']
  if (searchEngines.some(engine => referrer.includes(engine))) {
    return 'organic'
  }

  return 'referral'
}

/**
 * Track CTA clicks
 */
export const trackCTAClick = (
  ctaType: 'phone' | 'whatsapp' | 'email' | 'form' | 'chat' | 'messenger' | 'viber',
  params?: GAEventParams
) => {
  const eventName = ctaType === 'phone'
    ? 'phone_call_click'
    : ctaType === 'whatsapp'
    ? 'whatsapp_click'
    : ctaType === 'email'
    ? 'email_click'
    : ctaType === 'messenger'
    ? 'messenger_click'
    : ctaType === 'viber'
    ? 'viber_click'
    : 'contact_form_submit'

  trackEvent(eventName, {
    cta_type: ctaType,
    ...params,
  })
}

/**
 * Track property view
 */
export const trackPropertyView = (property: {
  id: string
  title: string
  property_type?: string
  listing_type?: 'sale' | 'rent' | string
  price?: number
  city?: string
  developer_name?: string
}) => {
  trackEvent('property_view', {
    property_id: property.id,
    property_title: property.title,
    property_type: property.property_type,
    listing_type: property.listing_type,
    property_price: property.price,
    property_location: property.city,
    developer_name: property.developer_name,
    value: property.price,
    currency: 'PHP',
  })
}

/**
 * Track property search
 */
export const trackPropertySearch = (params: {
  search_term?: string
  filters?: Record<string, any>
  results_count?: number
}) => {
  const filterNames = params.filters ? Object.keys(params.filters) : []

  trackEvent('property_search', {
    search_term: params.search_term,
    filters_applied: filterNames,
    results_count: params.results_count,
  })
}

/**
 * Track lead submission (high-value conversion)
 * Enhanced with proper GA4 conversion tracking
 */
export const trackLeadSubmission = (params: {
  lead_type: 'inquiry' | 'viewing' | 'contact' | 'subscription'
  property_id?: string
  property_title?: string
  property_type?: string
  listing_type?: 'sale' | 'rent' | string
  property_price?: number
  value?: number
}) => {
  // Track the main lead_submit event
  trackEvent('lead_submit', {
    ...params,
    currency: 'PHP',
    value: params.value || params.property_price || 0,
  })

  // ALSO track specific conversion events for GA4
  // These should be configured as conversions in GA4 dashboard
  if (params.lead_type === 'inquiry') {
    trackEvent('contact_form_submit', {
      ...params,
      currency: 'PHP',
      value: params.value || params.property_price || 0,
    })

    // Facebook Pixel conversion (if available)
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;(window as any).fbq('track', 'Contact', {
        content_name: params.property_title,
        content_category: params.property_type,
        value: params.value || params.property_price || 0,
        currency: 'PHP',
      })
    }
  } else if (params.lead_type === 'subscription') {
    trackEvent('subscription_payment', {
      ...params,
      currency: 'PHP',
      value: params.value || 0,
    })

    // Facebook Pixel conversion
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;(window as any).fbq('track', 'InitiateCheckout', {
        value: params.value || 0,
        currency: 'PHP',
      })
    }
  }
}

/**
 * Track scroll depth
 */
export const trackScrollDepth = (percentage: number) => {
  trackEvent('scroll_depth', {
    scroll_percentage: percentage,
  })
}

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('set', 'user_properties', properties)
}

/**
 * Set user ID (for logged-in users)
 */
export const setUserId = (userId: string) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('config', process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID!, {
    user_id: userId,
  })
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}
